import { endsWith } from 'lodash';
import { RECEIVED_ACCOUNTS } from 'state/account/AccountBalancesActions';

import { RECEIVED_CUSTOM_ACCOUNT_GROUPS } from 'state/preferences/PreferencesActions';
import {
  HISTORICAL_BALANCES_SUFFIX,
  RESET_HISTORICAL_BALANCES
} from './HistoricalBalanceActions';
import { SELECT_ACCOUNTS_PREFIX } from './SelectAccountsFilteringActions';
import {
  RECEIVED_CUSTOMER_PREFERENCES,
  RECEIVED_UPDATE_CUSTOMER_PREFERENCES
} from 'state/preferences/PreferencesActions';
import { RECEIVED_WORKSPACE_PREFERENCES } from 'state/Actions';

import { groupByIdMap } from 'services/Preferences';

export const CUSTOM_ACCOUNT_GROUP_PREFIX = 'custom-';

export const initialCustomizeAccountViewFilteringState = {
  sorting: {
    selection: 'default'
  },
  filtering: {
    selection: 'all'
  },
  grouping: {
    selection: 'product'
  }
};

export const initialDashboardTableFilteringState = {
  sorting: {
    selection: 'default'
  },
  filtering: {
    selection: 'all'
  },
  grouping: {
    selection: 'default'
  }
};

export const initialFilteringState = {
  sorting: {
    selection: 'default',
    options: [
      'default',
      'ticker',
      {
        label: 'Balance',
        value: 'marketValue'
      },
      {
        label: 'Change in Price',
        value: 'change'
      },
      {
        label: 'Daily Change ($)',
        value: 'dailyChange'
      },
      'price'
    ]
  },
  filtering: {
    selection: 'all',
    options: [
      {
        label: 'All Accounts',
        value: 'all'
      },
      {
        label: 'Retirement Advisory Service',
        value: 'activeAdvisory'
      },
      {
        label: 'Mutual Fund Accounts',
        value: 'mutualFund'
      },
      {
        label: 'Brokerage Accounts',
        value: 'brokerage'
      },
      {
        label: '529 College Savings Plan Accounts',
        value: '529'
      },
      {
        label: 'ActivePlus Portfolios',
        value: 'activePlus'
      },
      {
        label: 'Retirement Accounts',
        value: 'retirement'
      },
      {
        label: 'Non-Retirement Accounts',
        value: 'nonRetirement'
      },
      {
        label: 'Owned Accounts',
        value: 'owned'
      },
      {
        label: 'Related Accounts',
        value: 'related'
      },
      {
        label: 'View-Only Accounts',
        value: 'viewOnly'
      }
    ]
  },
  grouping: {
    selection: 'retirement',
    options: [
      {
        label: 'Retirement/Non-Retirement',
        value: 'retirement'
      },
      {
        label: 'Non-Retirement/Retirement',
        value: 'nonRetirement'
      },
      {
        label: 'Product',
        value: 'product'
      },
      {
        label: 'Ownership/Registration',
        value: 'ownership'
      }
    ]
  }
};


export const getUpdatedAccountTypeFilteringOptions = (accountTypeOptions, groups) => {
  const updatedOptions = accountTypeOptions && accountTypeOptions || [];
  groups && groups.map(group => {
    const index = accountTypeOptions.findIndex(option => group.label === option.label && group.value === option.value);
    if (index > -1) {
      updatedOptions[index] = group;
    } else {
      updatedOptions.push(group);
    }
  })
  return updatedOptions;
}
 
export const selectAccountsFilteringReducer = (
  state = initialFilteringState,
  action = { type: '', selection: '' },
  fullState = {}
) => {
  const { customAccountGroups, type, selection } = action;
  const { view = {} } = fullState;

  let { filtering, grouping, defaultGroupingBy } = state;

  if (type === RECEIVED_ACCOUNTS) {
    if (view.hasCollegeSavingsOnly && grouping.selection !== 'default') {
      grouping = { ...grouping, selection: 'default' };
    }

    return {
      ...state,
      filtering,
      grouping,
      defaultGroupingBy
    };
  }

  if (
    type === RECEIVED_CUSTOMER_PREFERENCES ||
    type === RECEIVED_UPDATE_CUSTOMER_PREFERENCES ||
    type === RECEIVED_WORKSPACE_PREFERENCES
  ) {
    const { customAccountGroups, defaultGroupBy } = action.preferences;
    const { grouping } = state;

    if (customAccountGroups) {
      const newGroups = customAccountGroups.map(
        ({ groupName, accountDescriptors }) => ({
          label: `${groupName}`,
          value: `${CUSTOM_ACCOUNT_GROUP_PREFIX}${groupName}`,
          accountIds: accountDescriptors.map((a) => a.accountId)
        })
      );

      const existingOptions = filtering.options.filter(
        (opt) => opt.label.indexOf(CUSTOM_ACCOUNT_GROUP_PREFIX) === -1
      );
      
      const updatedOptions = [...existingOptions]
      newGroups && newGroups.map(group => {
        const index = existingOptions.findIndex(option => group.label === option.label && group.value === option.value);
        if (index > -1) {
          updatedOptions[index] = group;
        } else {
          updatedOptions.push(group);
        }
      })
      return {
        ...state,
        filtering: {
          selection: state.filtering.selection,
          options: updatedOptions
        }
      };
    }

    if (defaultGroupBy && defaultGroupBy.accountGroupingId) {
      grouping.selection = groupByIdMap[defaultGroupBy.accountGroupingId];
      return {
        ...state,
        grouping
      };
    }

    return state;
  }

  if (
    type === RECEIVED_CUSTOM_ACCOUNT_GROUPS &&
    customAccountGroups &&
    customAccountGroups.length
  ) {

    const customGroup = customAccountGroups.map(({ name, accountIds }) => ({
      label: `${name}`,
      value: `${CUSTOM_ACCOUNT_GROUP_PREFIX}${name}`,
      accountIds
    }));
    
    filtering = {
      ...filtering,
      options: getUpdatedAccountTypeFilteringOptions(filtering.options, customGroup)
    };

    return {
      ...state,
      filtering,
      grouping,
      defaultGroupingBy
    };
  }

  if (!type.startsWith(SELECT_ACCOUNTS_PREFIX)) {
    return state;
  }

  const selectionType = type.replace(SELECT_ACCOUNTS_PREFIX, '').toLowerCase();
  const { options } = state[selectionType];
  let accountIds;

  if (selection.startsWith(CUSTOM_ACCOUNT_GROUP_PREFIX)) {
    accountIds = (options.find(({ value }) => value === selection) || {})
      .accountIds;
  }
  return {
    ...state,
    [selectionType]: {
      selection,
      options,
      accountIds
    }
  };
};
selectAccountsFilteringReducer.priority = 18;

export const setHistoricalBalances = (
  state,
  action,
  { historicalBalances }
) => {
  return endsWith(action.type, HISTORICAL_BALANCES_SUFFIX)
    ? action.type === RESET_HISTORICAL_BALANCES
      ? {
          selectedDate: null,
          balances: null
        }
      : {
          selectedDate: action.selectedDate || historicalBalances.selectedDate,
          balances: action.balances || historicalBalances.balances
        }
    : state;
};

setHistoricalBalances.priority = 17;
